import { DefaultOptions, QueryClient } from '@tanstack/react-query';

import { apiLogger, config } from '~/app';

export type RequestMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD';
export type RequestHeaders = HeadersInit;
export type RequestOptions = {
  signal?: AbortSignal;
  timeoutMs?: number;
};
export type RequestConfig = {
  method: RequestMethod;
  url: string;
  headers?: RequestHeaders;
  signal?: AbortSignal;
};

// Current auth token – set by AuthContext so the request interceptor can attach it at send time
// (avoids race where a request is sent before defaults were applied in the same render cycle)
let authTokenRef: string | undefined;

export const setAuthToken = (token: string | undefined): void => {
  authTokenRef = token;
};

/** Context passed to auth-failure handler when a protected request gets 401. */
export type AuthFailureContext = {
  status: number;
  url: string;
  config?: RequestConfig;
};

// Global callback for protected-request 401 – set by AuthContext. 403 is never passed (business auth).
let handleAuthError: ((context: AuthFailureContext) => void) | null = null;
let isHandlingAuthError = false; // Dedupe: concurrent 401s do not trigger multiple refresh attempts

export const setAuthErrorHandler = (handler: (context: AuthFailureContext) => void) => {
  handleAuthError = handler;
};

export const clearAuthErrorHandler = () => {
  handleAuthError = null;
  isHandlingAuthError = false;
};

const NETWORK_ERROR = 'NETWORK_ERROR' as const;
const TIMEOUT_ERROR = 'TIMEOUT_ERROR' as const;
const DEFAULT_TIMEOUT_MS = 20_000;
const BODYLESS_METHODS = new Set<RequestMethod>(['GET', 'HEAD']);

function normalizeNoResponseError(error: Error, isTimeout: boolean): RequestError {
  return {
    status: 0,
    code: isTimeout ? TIMEOUT_ERROR : NETWORK_ERROR,
    message: error.message ?? 'Network error',
  };
}

function asAbsoluteUrl(url: string): string {
  return new URL(url, config.baseUrl).toString();
}

function mergeHeaders(headers?: RequestHeaders): Headers {
  const merged = new Headers(headers ?? {});
  if (authTokenRef) {
    merged.set('Authorization', `Bearer ${authTokenRef}`);
  }
  return merged;
}

function withAbortTimeout(
  timeoutMs: number | undefined,
  signal: AbortSignal | undefined,
): {
  signal?: AbortSignal;
  cleanup: () => void;
  didTimeout: () => boolean;
} {
  const effectiveTimeout = timeoutMs ?? DEFAULT_TIMEOUT_MS;
  if (typeof AbortController === 'undefined' || effectiveTimeout <= 0) {
    return { signal, cleanup: () => {}, didTimeout: () => false };
  }
  const controller = new AbortController();
  let timeoutTriggered = false;

  const abortFromParent = () => controller.abort();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', abortFromParent, { once: true });
  }
  const timeoutId = setTimeout(() => {
    timeoutTriggered = true;
    controller.abort();
  }, effectiveTimeout);

  const cleanup = () => {
    if (timeoutId) clearTimeout(timeoutId);
    if (signal) signal.removeEventListener('abort', abortFromParent);
  };

  return { signal: controller.signal, cleanup, didTimeout: () => timeoutTriggered };
}

async function parseResponseBody<T>(response: Response): Promise<T | undefined> {
  if (response.status === 204 || response.status === 205) {
    return undefined;
  }
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    const text = await response.text();
    if (!text.trim()) return undefined;
    return JSON.parse(text) as T;
  }
  const text = await response.text();
  if (!text.trim()) return undefined;
  return text as T;
}

export const requestFn = async <T>(
  method: RequestMethod,
  url: string,
  data?: unknown,
  headers?: RequestHeaders,
  options?: RequestOptions,
): Promise<T> => {
  if (BODYLESS_METHODS.has(method) && data !== undefined) {
    throw new Error(`Request body is not allowed for ${method}`);
  }

  const requestUrl = asAbsoluteUrl(url);
  const requestConfig: RequestConfig = {
    method,
    url: requestUrl,
    headers,
    signal: options?.signal,
  };
  const requestHeaders = mergeHeaders(headers);
  const timeout = withAbortTimeout(options?.timeoutMs, options?.signal);

  const init: RequestInit = {
    method,
    credentials: 'include',
    headers: requestHeaders,
    signal: timeout.signal,
  };
  if (!BODYLESS_METHODS.has(method) && data !== undefined) {
    init.body = data instanceof FormData ? data : JSON.stringify(data);
    if (!(data instanceof FormData) && !requestHeaders.has('Content-Type')) {
      requestHeaders.set('Content-Type', 'application/json');
    }
  }

  try {
    const response = await fetch(requestUrl, init);
    const responseBody = await parseResponseBody<unknown>(response);

    if (!response.ok) {
      const messageResponse =
        responseBody && typeof responseBody === 'object'
          ? (responseBody as MessageResponse)
          : undefined;
      const requestError: RequestError = {
        status: response.status,
        message: messageResponse?.message ?? response.statusText,
        code: messageResponse?.code,
      };

      const isRefreshRequest = requestUrl.includes('/auth/refresh');
      if (response.status === 401 && !isRefreshRequest && handleAuthError && !isHandlingAuthError) {
        isHandlingAuthError = true;
        apiLogger.info(
          { status: response.status, url: requestUrl },
          'Global interceptor: 401 on protected request, notifying auth',
        );
        handleAuthError({ status: response.status, url: requestUrl, config: requestConfig });
        setTimeout(() => {
          isHandlingAuthError = false;
        }, 1000);
      }
      return Promise.reject(requestError);
    }

    return responseBody as T;
  } catch (e) {
    const error = e instanceof Error ? e : new Error('Network error');
    const abortError = error.name === 'AbortError';
    const timeoutError = timeout.didTimeout();
    if (abortError || timeoutError) {
      return Promise.reject(normalizeNoResponseError(error, true));
    }
    return Promise.reject(normalizeNoResponseError(error, false));
  } finally {
    timeout.cleanup();
  }
};

export const getFn = <T>(
  url: string,
  data?: unknown,
  headers?: RequestHeaders,
  options?: RequestOptions,
) => {
  return requestFn<T>('GET', url, data, headers, options);
};

export const postFn = async <T>(
  url: string,
  data?: unknown,
  headers?: RequestHeaders,
  options?: RequestOptions,
) => {
  return requestFn<T>('POST', url, data, headers, options);
};

export const putFn = async <T>(
  url: string,
  data?: unknown,
  headers?: RequestHeaders,
  options?: RequestOptions,
) => {
  return requestFn<T>('PUT', url, data, headers, options);
};

export const deleteFn = async <T>(
  url: string,
  data?: unknown,
  headers?: RequestHeaders,
  options?: RequestOptions,
) => {
  return requestFn<T>('DELETE', url, data, headers, options);
};

export type RequestError = { status: number } & Partial<MessageResponse>;
export type MessageResponse = { message?: string; code?: string };

export function isRequestError(error: unknown): error is RequestError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    typeof (error as RequestError).status === 'number'
  );
}

export function isNetworkError(error: unknown): boolean {
  if (!isRequestError(error)) return false;
  return error.status === 0 || error.code === NETWORK_ERROR;
}

export function isTimeoutError(error: unknown): boolean {
  if (!isRequestError(error)) return false;
  return error.status === 0 && error.code === TIMEOUT_ERROR;
}

export type TFunction = (key: string) => string;

export function getApiErrorMessage(error: unknown, t: TFunction): string {
  if (isTimeoutError(error)) {
    return t('error_network_timeout');
  }
  if (isNetworkError(error)) {
    return t('error_backend_unreachable');
  }
  if (isRequestError(error) && error.message && typeof error.message === 'string') {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return t('error_generic');
}

const retryDelay = (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000);

type ShouldRetryFunction<TError = unknown> = (failureCount: number, error: TError) => boolean;
export const retry: ShouldRetryFunction<unknown> = (failureCount, error): boolean => {
  if (!isRequestError(error)) {
    apiLogger.debug({ error }, 'blocked retry for non-request error');
    return false;
  }

  const { status } = error;
  if (status === 400) {
    apiLogger.debug('blocked retry for "400 Bad Request"');
    return false;
  }
  if (status === 401) {
    apiLogger.debug('blocked retry for "401 Unauthorized"');
    return false;
  }
  if (status === 404) {
    apiLogger.debug('blocked retry for "404 Not Found"');
    return false;
  }
  if (status === 429) {
    // Retrying into a rate limiter amplifies the storm that tripped it (4 attempts
    // per request, all counted against the same bucket). Fail fast instead; the
    // server sends Retry-After and staleTime / user action will re-trigger later.
    apiLogger.debug('blocked retry for "429 Too Many Requests"');
    return false;
  }
  if (status === 0) {
    if (failureCount <= 3) {
      apiLogger.debug({ failureCount }, 'retrying (network/transport)');
      return true;
    }
    apiLogger.debug('stop retrying after network/transport failures');
    return false;
  }
  if (failureCount <= 3) {
    apiLogger.debug({ failureCount }, 'retrying');
    return true;
  }
  apiLogger.debug('stop retrying');
  return false;
};

const defaultOptions: DefaultOptions = {
  queries: {
    staleTime: 30000,
    placeholderData: (previousData) => previousData,
    retryDelay,
    retry,
  },
  mutations: {
    retryDelay,
    retry,
  },
};

export const queryClient = new QueryClient({ defaultOptions });
